import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import type { PromptItem, Category } from '../utils/types'
import { getCategories } from '../utils/categoryUtils'
import { DEFAULT_CATEGORY_ID } from '../utils/constants'
import TagSelector from './TagSelector'

interface PromptFormProps {
  onSubmit: (prompt: PromptItem | Omit<PromptItem, 'id'>) => Promise<void>
  initialData: PromptItem | null
  onCancel: () => void
  isEditing: boolean
}

const PromptForm = ({ onSubmit, initialData, onCancel, isEditing }: PromptFormProps) => {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [notes, setNotes] = useState('')
  const [thumbnail, setThumbnail] = useState('')
  const [categoryId, setCategoryId] = useState(DEFAULT_CATEGORY_ID)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [loadingCategories, setLoadingCategories] = useState(true)

  // 加载分类列表
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true)
        const categoriesList = await getCategories()
        setCategories(categoriesList.filter(cat => cat.enabled)) // 只显示启用的分类
      } catch (err) {
        console.error('加载分类错误', err)
      } finally {
        setLoadingCategories(false)
      }
    }
    
    loadCategories()
  }, [])

  // Reset form when initialData changes (editing mode toggle)
  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title)
      setContent(initialData.content)
      setTags(initialData.tags || [])
      setNotes(initialData.notes || '')
      setThumbnail(initialData.thumbnail || '')
      setCategoryId(initialData.categoryId || DEFAULT_CATEGORY_ID)
    } else {
      // Clear form when not in edit mode
      setTitle('')
      setContent('')
      setTags([])
      setNotes('')
      setThumbnail('')
      setCategoryId(DEFAULT_CATEGORY_ID)
    }
    setError(null)
  }, [initialData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate form inputs
    if (!title.trim()) {
      setError('标题不能为空')
      return
    }

    if (!content.trim()) {
      setError('内容不能为空')
      return
    }

    if (!categoryId) {
      setError('请选择分类')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      // Create prompt object
      const promptData = {
        ...(initialData ? { id: initialData.id } : {}),
        title: title.trim(),
        content: content.trim(),
        tags: tags, // tags is already an array
        notes: notes.trim(),
        thumbnail: thumbnail.trim(),
        enabled: true,
        categoryId,
        lastModified: new Date().toISOString(),
      }

      await onSubmit(promptData as any) // Type assertion to handle both new and edited prompts

      // Clear form if not in edit mode (adding new prompt)
      if (!isEditing) {
        setTitle('')
        setContent('')
        setTags([])
        setNotes('')
        setThumbnail('')
        setCategoryId(DEFAULT_CATEGORY_ID)
      }
    } catch (err) {
      console.error('表单提交错误', err)
      setError('保存失败')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
      {error && (
        <div className='bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md mb-4 flex items-start'>
          <svg
            className='w-5 h-5 mr-2 mt-0.5 flex-shrink-0'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
            />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className='space-y-5'>
        <div>
          <label htmlFor='title' className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            标题
          </label>
          <input
            type='text'
            id='title'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 dark:bg-gray-700 dark:text-white'
            placeholder='输入提示词标题'
          />
        </div>

        <div>
          <label htmlFor='content' className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            内容
          </label>
          <textarea
            id='content'
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={6}
            className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 dark:bg-gray-700 dark:text-white'
            placeholder='输入提示词内容'
          />
          <div className='mt-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-md border border-gray-200 dark:border-gray-600'>
            <p>您可以在内容中使用变量，格式为 {`{{变量名}}`}</p>
            <p className='mt-1 text-gray-500 dark:text-gray-400 text-xs'>例如: 你现在是一个 {`{{角色}}`}，有着 {`{{年限}}`} 年的开发经验</p>
          </div>
        </div>

        <div>
          <label htmlFor='category' className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            分类
          </label>
          {loadingCategories ? (
            <div className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400'>
              加载分类中...
            </div>
          ) : (
            <select
              id='category'
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 dark:bg-gray-700 dark:text-white'
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          )}
          {categories.length === 0 && !loadingCategories && (
            <div className='mt-2 text-sm text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 p-3 rounded-md border border-orange-200 dark:border-orange-800'>
              <p>没有可用的分类。</p>
              <Link
                to='/categories'
                className='text-orange-700 dark:text-orange-300 underline hover:text-orange-800 dark:hover:text-orange-200'
              >
                点击这里创建一个分类
              </Link>
            </div>
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            标签
          </label>
          <TagSelector
            selectedTags={tags}
            onChange={setTags}
            placeholder='选择或输入标签...'
          />
        </div>

        <div>
          <label htmlFor='notes' className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            备注
          </label>
          <textarea
            id='notes'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 dark:bg-gray-700 dark:text-white'
            placeholder='添加备注信息（可选）'
          />
        </div>

        <div>
          <label htmlFor='thumbnail' className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
            缩略图地址
          </label>
          <input
            type='url'
            id='thumbnail'
            value={thumbnail}
            onChange={(e) => setThumbnail(e.target.value)}
            className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 dark:bg-gray-700 dark:text-white'
            placeholder='https://example.com/thumbnail.png（可选）'
          />
        </div>


        <div className='flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700'>
          <button
            type='button'
            onClick={onCancel}
            className='px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200'
          >
            取消
          </button>
          <button
            type='submit'
            disabled={isSubmitting}
            className={`px-6 py-2.5 text-sm font-medium text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ${
              isSubmitting
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg hover:-translate-y-0.5'
            }`}
          >
            {isSubmitting ? (
              <div className='flex items-center'>
                <div className='w-4 h-4 mr-2'>
                  <div className='border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                </div>
                保存中...
              </div>
            ) : (
              isEditing ? '更新提示词' : '创建提示词'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default PromptForm
